import {
  IconZoom,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { Loader, Stack, ActionIcon, Skeleton } from '@mantine/core'
import Meta from '../common/Meta'
import classes from './Ranks.module.css'
import { usePlayerRanks, useTeamRanks } from './api'
import { useState, useEffect, Suspense } from 'react'
import RanksComponent from '../stories/Ranks/Ranks'
import { useMediaQuery, useDebouncedValue } from '@mantine/hooks'
import clsx from 'clsx'
import Header from '../common/components/header/Header'
import Tabs from '../stories/Tabs/Tabs'
import { useCompetitionId } from '../common/hooks'
import { useCompetitionDetails } from '../navigation/api'
import { useDivisions } from '../ladder/api'
import { useAllTeams } from '../teams/api'
import { useSearchParams, useNavigate, useParams } from 'react-router'
import { Typography } from '../common/typography'
import { Dropdown } from '../common/components/dropdown/Dropdown'
import { Input } from '../common/components/input/StyledTextInput'

function TeamRanksResults({
  competition_id,
  division_id,
  page,
  pageSize,
  search,
  activeTab,
}: {
  competition_id: number
  division_id?: number
  page: number
  pageSize: number
  search: string
  activeTab: string
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: teamRanksResponse } = useTeamRanks({
    competition_id,
    division_id,
    page,
    page_size: pageSize,
    search,
  })

  const items = teamRanksResponse.items

  if (items.length === 0) {
    return (
      <Typography fs="sm" fc={{ light: 'gray-6', dark: 'gray-6' }} ta="center">
        {search ? `No teams found matching "${search}"` : 'No teams found'}
      </Typography>
    )
  }

  return (
    <RanksComponent
      ranks={{ type: 'team', data: items }}
      active={!isDesktop ? activeTab : undefined}
      subranks={!!division_id}
    />
  )
}

function PlayerRanksResults({
  competition_id,
  team_id,
  page,
  pageSize,
  search,
  activeTab,
}: {
  competition_id: number
  team_id?: number
  page: number
  pageSize: number
  search: string
  activeTab: string
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: playerRanksResponse } = usePlayerRanks({
    competition_id,
    team_id,
    page,
    page_size: pageSize,
    search,
  })

  const items = playerRanksResponse.items

  if (items.length === 0) {
    return (
      <Typography fs="sm" fc={{ light: 'gray-6', dark: 'gray-6' }} ta="center">
        {search ? `No players found matching "${search}"` : 'No players found'}
      </Typography>
    )
  }

  return (
    <RanksComponent
      ranks={{ type: 'player', data: items }}
      active={!isDesktop ? activeTab : undefined}
      subranks={!!team_id}
    />
  )
}

function TeamPaginationControls({
  competition_id,
  division_id,
  page,
  pageSize,
  search,
}: {
  competition_id: number
  division_id?: number
  page: number
  pageSize: number
  search: string
}) {
  const [, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: teamRanksResponse } = useTeamRanks({
    competition_id,
    division_id,
    page,
    page_size: pageSize,
    search,
  })

  const total = teamRanksResponse.total
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePrevPage = () => {
    if (page > 1) {
      setSearchParams({ page: (page - 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setSearchParams({ page: (page + 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (total === 0) return null

  return (
    <div
      className={clsx(classes.paginationContainer, {
        [classes.paginationContainerMobile]: !isDesktop,
      })}
    >
      <Typography fs="sm" lh="140">
        {startItem}-{endItem} of {total}
      </Typography>

      <ActionIcon
        variant="default"
        onClick={handlePrevPage}
        disabled={page === 1}
        className={classes.paginationIcon}
      >
        <IconChevronLeft
          data-disabled={page === 1}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>

      <ActionIcon
        variant="default"
        onClick={handleNextPage}
        disabled={page >= totalPages}
        className={classes.paginationIcon}
      >
        <IconChevronRight
          data-disabled={page >= totalPages}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
    </div>
  )
}

function PlayerPaginationControls({
  competition_id,
  team_id,
  page,
  pageSize,
  search,
}: {
  competition_id: number
  team_id?: number
  page: number
  pageSize: number
  search: string
}) {
  const [, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: playerRanksResponse } = usePlayerRanks({
    competition_id,
    team_id,
    page,
    page_size: pageSize,
    search,
  })

  const total = playerRanksResponse.total
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePrevPage = () => {
    if (page > 1) {
      setSearchParams({ page: (page - 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setSearchParams({ page: (page + 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (total === 0) return null

  return (
    <div
      className={clsx(classes.paginationContainer, {
        [classes.paginationContainerMobile]: !isDesktop,
      })}
    >
      <Typography fs="sm" lh="140">
        {startItem}-{endItem} of {total}
      </Typography>

      <ActionIcon
        variant="default"
        onClick={handlePrevPage}
        disabled={page === 1}
        className={classes.paginationIcon}
      >
        <IconChevronLeft
          data-disabled={page === 1}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>

      <ActionIcon
        variant="default"
        onClick={handleNextPage}
        disabled={page >= totalPages}
        className={classes.paginationIcon}
      >
        <IconChevronRight
          data-disabled={page >= totalPages}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
    </div>
  )
}

export default function Ranks() {
  const competitionId = useCompetitionId()
  const { tab } = useParams()
  const navigate = useNavigate()
  const [selectedDivision, setSelectedDivision] = useState('')
  const [selectedTeam, setSelectedTeam] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const pageSize = 20

  const { data: divisions } = useDivisions({
    competition_id: competitionId,
  })

  const { data: teams } = useAllTeams({
    competition_id: competitionId,
  })

  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [searchValue, setSearchValue] = useState('')
  const [viewTab, setViewTab] = useState<string>('full')

  const [debouncedSearch] = useDebouncedValue(searchValue, 500)

  useEffect(() => {
    if (currentPage !== 1) {
      setSearchParams({ page: '1' })
    }
  }, [debouncedSearch, selectedDivision, selectedTeam])

  const handleTabChange = (value: string) => {
    navigate(`/competitions/${competitionId}/ranks/${value}`)
    setSelectedDivision('')
    setSelectedTeam('')
    setSearchValue('')
  }

  const { data: competitionDetails } = useCompetitionDetails({
    competition_id: competitionId,
  })

  const rawTab = tab === 'players' ? 'players' : 'teams'
  const activeTab = (
    competitionDetails?.single_player_teams ? 'teams' : rawTab
  ) as 'teams' | 'players'

  const viewTabs = [
    {
      value: 'full',
      label: 'Full view',
    },
    {
      value: 'short',
      label: 'Short view',
    },
  ]

  const mainTabs = [
    {
      value: 'teams',
      label: 'Teams',
    },
    {
      value: 'players',
      label: 'Players',
    },
  ]

  return (
    <div className={classes.container}>
      <Meta title="Overall Rank" />
      <Header title="Overall Rank" withUpdate />
      <div className={classes.container_wrapper}>
        <div
          className={clsx(classes.container_wrapper, {
            [classes.container_wrapper_mobile]: !isDesktop,
          })}
        >
          <div className={classes.top_section}>
            {!competitionDetails?.single_player_teams && (
              <Tabs
                items={mainTabs}
                value={activeTab}
                onChange={handleTabChange}
                isLarge
              />
            )}

            <div
              className={clsx(classes.search_wrapper, {
                [classes.search_wrapper_mobile]: !isDesktop,
              })}
            >
              <div className={classes.filtersContainer}>
                {activeTab === 'teams' && divisions && divisions.length > 0 && (
                  <Dropdown
                    placeholder="All divisions"
                    value={selectedDivision}
                    onChange={(value) => setSelectedDivision(value ?? '')}
                    data={[
                      { value: '', label: 'All divisions' },
                      ...divisions.map((division) => ({
                        value: division.division_id.toString(),
                        label: division.name,
                      })),
                    ]}
                    styles={{ root: { maxWidth: '15rem' } }}
                  />
                )}

                {activeTab === 'players' && teams && teams.items.length > 0 && (
                  <Dropdown
                    placeholder="All teams"
                    value={selectedTeam}
                    onChange={(value) => setSelectedTeam(value ?? '')}
                    data={[
                      { value: '', label: 'All teams' },
                      ...teams.items.map((team) => ({
                        value: team.team_id.toString(),
                        label: team.name,
                      })),
                    ]}
                    styles={{ root: { maxWidth: '15rem' } }}
                  />
                )}

                <Input
                  placeholder="Search"
                  value={searchValue}
                  onChange={(event) =>
                    setSearchValue(event.currentTarget.value)
                  }
                  rightSection={<IconZoom className={classes.zoom_icon} />}
                  rightSectionPointerEvents="none"
                  styles={{ root: { maxWidth: '15rem' } }}
                />
              </div>

              <Suspense
                fallback={
                  <Skeleton height={32} width={150} style={{ flexShrink: 0 }} />
                }
              >
                {activeTab === 'teams' ? (
                  <TeamPaginationControls
                    competition_id={competitionId}
                    division_id={
                      selectedDivision ? Number(selectedDivision) : undefined
                    }
                    page={currentPage}
                    pageSize={pageSize}
                    search={debouncedSearch}
                  />
                ) : (
                  <PlayerPaginationControls
                    competition_id={competitionId}
                    team_id={selectedTeam ? Number(selectedTeam) : undefined}
                    page={currentPage}
                    pageSize={pageSize}
                    search={debouncedSearch}
                  />
                )}
              </Suspense>
            </div>
          </div>

          {!isDesktop && (
            <Tabs
              items={viewTabs}
              value={viewTab}
              onChange={setViewTab}
              isSmall
            />
          )}

          <Suspense
            fallback={
              <Stack w="full" h="400px" justify="center" align="center">
                <Loader color="#0B6B7B" />
              </Stack>
            }
          >
            {activeTab === 'teams' ? (
              <TeamRanksResults
                competition_id={competitionId}
                division_id={
                  selectedDivision ? Number(selectedDivision) : undefined
                }
                page={currentPage}
                pageSize={pageSize}
                search={debouncedSearch}
                activeTab={viewTab}
              />
            ) : (
              <PlayerRanksResults
                competition_id={competitionId}
                team_id={selectedTeam ? Number(selectedTeam) : undefined}
                page={currentPage}
                pageSize={pageSize}
                search={debouncedSearch}
                activeTab={viewTab}
              />
            )}
          </Suspense>
        </div>
      </div>
    </div>
  )
}
